/**
 * maskGrid.js — геометрия маски половинного шага и чтение HU для неё.
 * Чистая математика без импортов; проверяется в node (tests/maskgrid_test.mjs).
 *
 * ЗАЧЕМ. Слова владельца: «очень большие тома… 214 МБ на одну маску — предел,
 * который настройкой не снимается». 214 МБ — это сам labelmap: Uint8-том
 * 512×512×818. Маска половинного шага по каждой оси — 256×256×409 = 26.8 МБ,
 * в ВОСЕМЬ раз меньше, и по разведке захода 0 движок такую маску рисует и
 * красит обычными кистями без правок (свой vtkImageMapper, индексы по
 * imageData самой маски). Этот файл — та математика, на которой полурешётка
 * стоит: её размеры, её начало в мире и честное чтение HU для неё.
 *
 * НАЧАЛО ПОЛУРЕШЁТКИ — не то же самое, что начало тома. Воксель (0,0,0)
 * полурешётки накрывает мелкие воксели {0,1}³, его ЦЕНТР лежит в мелких
 * координатах (0.5, 0.5, 0.5). Пропусти этот сдвиг — и вся маска уедет на
 * полшага мелкого вокселя по каждой оси: на глаз почти незаметно, в числах —
 * систематическая ошибка границы. Поэтому сдвиг закреплён тестом на мировых
 * координатах, а не оставлен на веру.
 *
 * HU ДЛЯ ГРУБОГО ВОКСЕЛЯ — среднее его мелкой восьмёрки (на краях — тех, что
 * есть). Ровно так же считает уменьшение вдвое в deformReg: грубый воксель
 * «видит» среднее того, что накрывает. Брать один угловой мелкий воксель —
 * значит решать порог по смещённой точке.
 */

/** Размеры/шаг/начало полурешётки по мелкой сетке. direction — 9 чисел (колонки осей). */
export function полурешётка({ dims, spacing, origin, direction }) {
  if (!dims || dims.length !== 3 || !spacing || spacing.length !== 3) {
    return { ok: false, причина: 'нужны dims[3] и spacing[3]' };
  }
  const d = direction && direction.length === 9 ? direction : [1, 0, 0, 0, 1, 0, 0, 0, 1];
  const o = origin && origin.length === 3 ? origin : [0, 0, 0];
  const [sx, sy, sz] = spacing;
  const dims2 = [Math.ceil(dims[0] / 2), Math.ceil(dims[1] / 2), Math.ceil(dims[2] / 2)];
  const spacing2 = [sx * 2, sy * 2, sz * 2];
  // Центр грубого (0,0,0) = мелкие координаты (0.5,0.5,0.5):
  // world = origin + 0.5·(sx·колX + sy·колY + sz·колZ).
  const origin2 = [
    o[0] + 0.5 * (sx * d[0] + sy * d[3] + sz * d[6]),
    o[1] + 0.5 * (sx * d[1] + sy * d[4] + sz * d[7]),
    o[2] + 0.5 * (sx * d[2] + sy * d[5] + sz * d[8]),
  ];
  return { ok: true, dims: dims2, spacing: spacing2, origin: origin2, direction: [...d] };
}

/** Это полурешётка того тома? (ceil-половина по каждой оси) */
export function этоПолурешётка(dimsМаски, dimsТома) {
  if (!dimsМаски || !dimsТома) return false;
  for (let a = 0; a < 3; a++) if (dimsМаски[a] !== Math.ceil(dimsТома[a] / 2)) return false;
  return true;
}

/** HU того же индекса (маска и том на одной сетке). */
export function huПрямое(scalar, dimsFine) {
  const [nx, ny] = dimsFine;
  return (x, y, z) => scalar[x + y * nx + z * nx * ny];
}

/**
 * HU для грубого вокселя полурешётки: среднее его мелкой восьмёрки
 * (2x..2x+1)×(2y..2y+1)×(2z..2z+1), на краях — среднее доступных.
 */
export function huСреднее2х2х2(scalar, dimsFine) {
  const [nx, ny, nz] = dimsFine;
  return (x, y, z) => {
    const x0 = 2 * x, y0 = 2 * y, z0 = 2 * z;
    let сумма = 0, штук = 0;
    for (let dz = 0; dz < 2; dz++) {
      const Z = z0 + dz; if (Z >= nz) continue;
      for (let dy = 0; dy < 2; dy++) {
        const Y = y0 + dy; if (Y >= ny) continue;
        for (let dx = 0; dx < 2; dx++) {
          const X = x0 + dx; if (X >= nx) continue;
          сумма += scalar[X + Y * nx + Z * nx * ny]; штук++;
        }
      }
    }
    return штук ? сумма / штук : NaN;
  };
}
